import React from 'react'
import { useSelector } from 'react-redux'
import { Card } from 'transfix-ui/components/Card'
import { Box, makeStyles } from 'transfix-ui/core'
import { ICarrierState } from 'types/carrier'
import { IHighwayContact } from 'types/graphqlTypes'
import { useUserDetails } from 'utils/hooks/useUserDetails'
import { IStore } from 'apps/carrier/utils/reducers'
import { OpenInNewTabIcon } from 'transfix-icons'
import { formatSnakeCaseStringToTitleCase } from 'utils/formatSnakeCaseStringToTitleCase'
import { DISPLAY } from 'constants/display'
import { useHighwayCarrierQuery } from './graphql/HighwayCarrier'

const useStyles = makeStyles({
  noWrap: {
    whiteSpace: 'nowrap',
  },
})

export default function HighwayCarrier() {
  const classes = useStyles()
  const { userPermissions } = useUserDetails()
  const { canEditComplianceCarrierInfo } = userPermissions || {}
  const { carrierDetails } = useSelector<IStore, ICarrierState>(({ carrier }) => carrier)
  const { id: carrierId = '' } = carrierDetails || {}

  const { data, error, loading } = useHighwayCarrierQuery({
    variables: { carrierApplicationId: carrierId },
  })

  const { highwayCarrier: goHighwayCarrierInfo } = data || {}

  const {
    brokerAuthority,
    contacts,
    dbaName,
    driverOosInspections,
    ein,
    fatalCrashes,
    fmcsaContactPhone,
    highwayId,
    injuryCrashes,
    legalName,
    operatingStatus,
    rulesAssessmentPass,
    rulesAssessments,
    safetyRating,
  } = goHighwayCarrierInfo || {}

  const formattedEin = canEditComplianceCarrierInfo ? ein : ein?.replace(/\d(?=\d{4})/g, '*')

  const dispatcher =
    contacts?.find(contact => contact.isType === 'dispatch') || ({} as IHighwayContact)
  const { emailAddress: dispatcherEmailAddress, name: dispatcherName } = dispatcher || {}

  return (
    <Card>
      <div className='pv4'>
        <h3>Highway Carrier Information (Beta)</h3>
      </div>
      {!loading && !error && (
        <>
          <div>
            <div className='row no-gutter'>
              <div className='col-xs-8'>
                <small>Contact Phone</small>
                <div>{fmcsaContactPhone || DISPLAY.empty}</div>
              </div>
              <div className='col-xs-8'>
                <small>Company Name</small>
                <div>{legalName}</div>
              </div>
            </div>
            <div className='row no-gutter'>
              <div className='="col-xs-8'>
                <small>Website</small>
                <a
                  className='flex items-center'
                  href={`https://gohighway.com/broker/carriers/${highwayId}`}
                  target='_blank'
                  rel='noreferrer'>
                  <div className='pr2'>Highway</div>
                  <OpenInNewTabIcon color='skyBlue6' />
                </a>
              </div>
            </div>
            <div className='row no-gutter'>
              <div className='col-xs-8'>
                <small>Dispatcher Name</small>
                <div>{dispatcherName || DISPLAY.empty}</div>
              </div>
              <div className='col-xs-8'>
                <small>Dispatcher Email</small>
                <div>{dispatcherEmailAddress || DISPLAY.empty}</div>
              </div>
            </div>
            <div className='row no-gutter'>
              <div className='col-xs-8'>
                <small>Injury Crashes</small>
                <div>{injuryCrashes ?? DISPLAY.empty}</div>
              </div>
              <div className='col-xs-8'>
                <small>Fatal Crashes</small>
                <div>{fatalCrashes ?? DISPLAY.empty}</div>
              </div>
            </div>
            <div className='row no-gutter'>
              <div className='col-xs-8'>
                <small>Broker Authority</small>
                <div>{brokerAuthority || DISPLAY.empty}</div>
              </div>
              <div className='col-xs-8'>
                <small>Safety Rating</small>
                <div>{safetyRating || DISPLAY.empty}</div>
              </div>
            </div>
            <div className='row no-gutter'>
              <div className='col-xs-8'>
                <small>DBA Name</small>
                <div>{dbaName || DISPLAY.empty}</div>
              </div>
              <div className='col-xs-8'>
                <small>Operating Status</small>
                <div>{operatingStatus || DISPLAY.empty}</div>
              </div>
            </div>
            <div className='row no-gutter'>
              <div className='col-xs-8'>
                <small>Driver OOS Inspections</small>
                <div>{driverOosInspections ?? DISPLAY.empty}</div>
              </div>
              <div className='col-xs-8'>
                <small>EIN</small>
                <div>{formattedEin || DISPLAY.empty}</div>
              </div>
            </div>
            <div className='row no-gutter mt-2'>
              <div className='col-xs-8'>
                <strong>Highway Assessment</strong>
                <div>{rulesAssessmentPass ? 'Pass' : 'Fail'}</div>
              </div>
            </div>
            <Box ml={2}>
              {rulesAssessments?.map(rule => {
                const { result, ruleName } = rule
                return rule.ruleName === 'sets_requiring_override_file' ? null : (
                  <div className='row no-gutter' key={ruleName}>
                    <div className='col-xs-8'>
                      <small className={classes.noWrap}>
                        {formatSnakeCaseStringToTitleCase(ruleName)}
                      </small>
                      <div>{result || DISPLAY.empty}</div>
                    </div>
                  </div>
                )
              })}
            </Box>
          </div>
        </>
      )}
    </Card>
  )
}
